import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system/src/lib/messages/message-box';
import {
  IBillingAccount,
  paymentService,
  IOgoneFeedbackParameters,
} from '@dch/billing-business-logic';
import { CustomerAccount } from '@dch/common-business-logic';
import {
  AnalyticsService,
  EnvironmentService,
  TranslationService,
} from '@dch/ng-common';
import isEqual from 'lodash.isequal';
import {
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  mergeMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getBillingBalanceRequest,
  setSelectedBillingAccount,
} from 'src/app/state/actions/billing-balance.actions';
import { updateBillingPaymentRequest } from 'src/app/state/actions/payment.actions';
import { ApplicationState } from 'src/app/state/reducers';
import {
  selectHasErrorForTransactionOrPaymentInfoByAccountNumber,
  isFetchingTransactionOrPaymentInfoByAccountNumber,
  selectBillingBalance,
  selectHasEmptyInstallbase,
  selectSelectedBillingAccount,
} from 'src/app/state/selectors/billing-balance.selector';
import { selectCustomerAccountState } from 'src/app/state/selectors/customer-account.selector';
import { OnDestroyHookDirective } from '../common/hooks';
import { RemoteLoggerService } from '../services/remote-logger.service';
import {
  getBillingPaymentInfoRequest,
  getBillingTransactionsRequest,
} from 'src/app/state/actions';
import { combineLatest } from 'rxjs';
import { selectPaymentData } from 'src/app/state/selectors/payment-data.selectors';
import {
  BALANCE_CALL_ERROR,
  BILLING_SERVICES_ERROR,
  TAGGED_ERROR_KEY,
  USER_CALL_ERROR,
} from '../config/my-bill.constants';

@Component({
  selector: 'my-bill-overview-container',
  templateUrl: './my-bill-overview-container.component.html',
  standalone: false,
})
export class MyBillOverviewContainerComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  readonly selectCustomerAccountState$ = this.store.select(
    selectCustomerAccountState
  );
  readonly selectPaymentData$ = this.store.select(selectPaymentData);

  readonly selectedBillingAccount$ = this.store
    .select(selectSelectedBillingAccount)
    .pipe(distinctUntilChanged((a, b) => isEqual(a, b)));
  readonly billingBalance$ = this.store
    .select(selectBillingBalance)
    .pipe(distinctUntilChanged((a, b) => isEqual(a, b)));
  readonly hasEmptyInstallbase$ = this.store.select(selectHasEmptyInstallbase);

  get isAccountSelectedWithMoreThanOneAccount(): boolean {
    return (
      this.billingAccounts?.length > 1 &&
      this.billingAccountNumberUrlParam !== undefined
    );
  }

  isOverviewLoadingSkeletonDisplayed = true;
  isBalanceLoadingSkeletonDisplayed = true;
  hasBalanceFetchingError = false;
  hasTransactionsOrPaymentInfoFetchingError = false;
  billingAccounts?: Array<IBillingAccount>;
  customerAccount?: CustomerAccount;
  isResUser: boolean;
  isUr: boolean;
  billingAccountNumberUrlParam?: string;
  hasAccountSelectionFromUrlParamBeenDispatched: boolean;
  mainTitle: string;
  messageBoxType: MessageBoxTypesEnum;
  messageBoxTitleKey: string;
  messageBoxContentKey: string;
  messageBoxContent: string;
  paymentMade: boolean;
  selectedBillingAccount?: IBillingAccount;
  isEvolutionScreenReady: boolean;
  paymentDataCallFailedStatus: boolean;

  constructor(
    private store: Store<ApplicationState>,
    private route: ActivatedRoute,
    private translationService: TranslationService,
    private remoteLoggerService: RemoteLoggerService,
    private router: Router,
    private environmentService: EnvironmentService,
    private analytics: AnalyticsService,
    private _location: Location
  ) {
    super();
  }

  ngOnInit() {
    this.fetchTheBalance();
    this.setHasEmptyInstallBaseMessage();
    this.setTheBillingAccountParameter();
    this.setTheSelectedAccount();
    this.fetchTransactionsForTheSelectedAccount();
    this.updateTheFetchingAndErrorStatusForTheSelectedAccount();

    this.addSubscription(
      this.route.queryParams.subscribe((params) => {
        if (params.STATUS !== undefined) {
          this.paymentMade = true;
          this.dispatchPaymentStatusAlert(params.STATUS);
        }
      })
    );

    this.addSubscription(
      this.selectPaymentData$
        .pipe(
          tap((value) => {
            if (value.hasError) {
              this.paymentDataCallFailedStatus = true;
              this.messageBoxType = MessageBoxTypesEnum.Error;
              this.messageBoxTitleKey = 'bills.toast.paymentData.failureTitle';
              this.messageBoxContentKey = 'bills.toast.paymentData.failure';
            } else {
              this.paymentDataCallFailedStatus = false;
            }
          })
        )
        .subscribe()
    );

    this.addSubscription(
      this.billingBalance$
        .pipe(
          filter((billingBalanceState) =>
            Boolean(billingBalanceState.viewableBillingAccounts.length > 0)
          ),
          withLatestFrom(this.route.queryParams),
          filter(([billingBalanceState, params]) =>
            paymentService.includesOgoneMandatoryFeedbackParameters(
              params as IOgoneFeedbackParameters
            )
          ),
          take(1),
          tap(([billingBalanceState, params]) => {
            //Note: billingAccountNumberUrlParam not present
            //when you come from pxplus webview , as fallback reading it from ogone feedback parameters
            if (
              this.billingAccountNumberUrlParam === undefined &&
              (this.environmentService.isProximusPlusMobileApp ||
                this.environmentService.isMobile)
            ) {
              this.billingAccountNumberUrlParam = params.billingAccountNumber;
              this.updateSelectedAccountWhenThereIsParameter();
            }
            const billingAccount =
              billingBalanceState.viewableBillingAccounts.find(
                (account) =>
                  account.accountNumber === this.billingAccountNumberUrlParam
              );
            if (billingAccount) {
              this.store.dispatch(
                updateBillingPaymentRequest({
                  billingAccountNumber: billingAccount.accountNumber,
                  requestBody:
                    paymentService.convertToUpdateBillingPaymentRequestBody(
                      params
                    ),
                })
              );
            }
            return params;
          }),
          distinctUntilChanged((a, b) => isEqual(a, b)),
          tap(() =>
            //Note: used here to replace the url in the address bar
            this._location.go(
              'overview/fls/' + this.billingAccountNumberUrlParam
            )
          )
        )
        .subscribe()
    );
    this.checkTheVisibilityOfEvolutionTab();
  }

  onClose() {
    this.paymentMade = false;
  }

  private checkTheVisibilityOfEvolutionTab() {
    this.isEvolutionScreenReady =
      'true' ===
      this.translationService.getTranslatedValueFor(
        'bills.evolutionChart.web.isEvolutionScreenReady'
      );
    if (
      this.environmentService.isProximusPlusMobileApp &&
      this.isEvolutionScreenReady
    ) {
      this.isEvolutionScreenReady =
        'true' ===
        this.translationService.getTranslatedValueFor(
          'bills.evolutionChart.web.isShownInPxPlus'
        );
    }
  }

  private fetchTheBalance() {
    this.addSubscription(
      combineLatest([this.selectCustomerAccountState$, this.billingBalance$])
        .pipe(
          tap(([customerAccountState, billingBalance]) => {
            if (
              !customerAccountState.isFetching &&
              customerAccountState.customerAccount?.getSelectedCustomer()
            ) {
              this.customerAccount = customerAccountState.customerAccount;
              if (
                !this.customerAccount?.isUr() &&
                billingBalance.billingBalance?.result === null &&
                !billingBalance.billingBalance.hasError
              ) {
                this.store.dispatch(getBillingBalanceRequest());
              } else if (this.customerAccount?.isUr()) {
                this.isUr = true;
                this.messageBoxType = MessageBoxTypesEnum.Information;
                this.messageBoxTitleKey = 'bills.URView.explicit.buttonTitle';
                this.messageBoxContent =
                  this.translationService.getTranslatedValueFor(
                    'bills.URView.explicit.message'
                  );
              }
              this.isResUser =
                this.customerAccount?.getSelectedCustomer().subsegment ===
                'RES';
              this.setPageTitle();
            } else if (
              !customerAccountState.isFetching &&
              customerAccountState.hasError
            ) {
              this.hasBalanceFetchingError = true;
              this.messageBoxType = MessageBoxTypesEnum.Error;
              this.messageBoxTitleKey = 'bills.redesign.errors.title';
              this.messageBoxContentKey = 'bills.redesign.errors.description';
              if (
                USER_CALL_ERROR !== sessionStorage.getItem(TAGGED_ERROR_KEY)
              ) {
                sessionStorage.setItem(TAGGED_ERROR_KEY, USER_CALL_ERROR);
                this.analytics.reportEvent(
                  this.getTaggingData(USER_CALL_ERROR, BILLING_SERVICES_ERROR)
                );
                this.remoteLoggerService.logError('user call failed');
              }
            }
          })
        )
        .subscribe()
    );
  }

  private setHasEmptyInstallBaseMessage() {
    this.addSubscription(
      this.hasEmptyInstallbase$
        .pipe(
          tap((value) => {
            if (value) {
              this.messageBoxType = MessageBoxTypesEnum.Information;
              this.messageBoxTitleKey = 'bills.ServiceUnavailable.message';
              this.analytics.reportEvent(
                this.getTaggingData(
                  'info message: no product found on this account'
                )
              );
            }
          })
        )
        .subscribe()
    );
  }

  private setTheBillingAccountParameter() {
    this.addSubscription(
      this.route.params
        .pipe(map(({ billingAccountNumber }) => billingAccountNumber))
        .subscribe((billingAccountNumber) => {
          this.billingAccountNumberUrlParam = billingAccountNumber;
        })
    );
  }

  private setTheSelectedAccount() {
    this.addSubscription(
      this.billingBalance$
        .pipe(
          tap((billingBalanceState) => {
            this.billingAccounts = billingBalanceState?.viewableBillingAccounts;

            this.isBalanceLoadingSkeletonDisplayed =
              billingBalanceState?.billingBalance.isFetching;

            if (
              !billingBalanceState.billingBalance.isFetching &&
              !billingBalanceState.billingBalance.hasError &&
              this.billingAccountNumberUrlParam !== undefined &&
              this.selectedBillingAccount === undefined
            ) {
              this.store.dispatch(
                setSelectedBillingAccount({
                  billingAccountNumber: this.billingAccountNumberUrlParam,
                })
              );
            } else if (billingBalanceState.billingBalance.hasError) {
              this.hasBalanceFetchingError = true;
              this.messageBoxType = MessageBoxTypesEnum.Error;
              this.messageBoxTitleKey = 'bills.redesign.errors.title';
              this.messageBoxContentKey = 'bills.redesign.errors.description';
              if (
                BALANCE_CALL_ERROR !== sessionStorage.getItem(TAGGED_ERROR_KEY)
              ) {
                sessionStorage.setItem(TAGGED_ERROR_KEY, BALANCE_CALL_ERROR);
                this.analytics.reportEvent(
                  this.getTaggingData(
                    BALANCE_CALL_ERROR,
                    BILLING_SERVICES_ERROR
                  )
                );
              }
            }
          })
        )
        .subscribe()
    );
  }

  private fetchTransactionsForTheSelectedAccount() {
    this.addSubscription(
      this.selectedBillingAccount$
        .pipe(
          distinctUntilChanged(),
          tap((billingAccount) => {
            this.selectedBillingAccount = billingAccount;
            if (billingAccount) {
              this.store.dispatch(
                getBillingTransactionsRequest(billingAccount.accountNumber)
              );
              this.store.dispatch(
                getBillingPaymentInfoRequest({
                  billingAccountNumber: billingAccount.accountNumber,
                })
              );
            }
          })
        )
        .subscribe()
    );
  }

  private updateTheFetchingAndErrorStatusForTheSelectedAccount() {
    this.addSubscription(
      this.selectedBillingAccount$
        .pipe(
          distinctUntilChanged(),
          filter((billingAccount) => billingAccount !== undefined),
          mergeMap((billingAccount) => {
            return this.store
              .select(
                isFetchingTransactionOrPaymentInfoByAccountNumber(
                  billingAccount.accountNumber
                )
              )
              .pipe(
                tap(
                  (isFetching) =>
                    (this.isOverviewLoadingSkeletonDisplayed = isFetching)
                )
              );
          })
        )
        .subscribe()
    );

    this.addSubscription(
      this.selectedBillingAccount$
        .pipe(
          distinctUntilChanged(),
          filter((billingAccount) => billingAccount !== undefined),
          mergeMap((billingAccount) => {
            return this.store
              .select(
                selectHasErrorForTransactionOrPaymentInfoByAccountNumber(
                  this.selectedBillingAccount.accountNumber
                )
              )
              .pipe(
                distinctUntilKeyChanged('failedService'),
                tap((error) => {
                  if (error !== null && error.hasError) {
                    this.hasTransactionsOrPaymentInfoFetchingError = true;
                    this.messageBoxType = MessageBoxTypesEnum.Error;
                    this.messageBoxTitleKey = 'bills.redesign.errors.title';
                    this.messageBoxContentKey =
                      'bills.redesign.errors.description';
                    if (
                      error?.failedService !== null &&
                      error.failedService !==
                        sessionStorage.getItem(TAGGED_ERROR_KEY)
                    ) {
                      sessionStorage.setItem(
                        TAGGED_ERROR_KEY,
                        error.failedService
                      );
                      this.analytics.reportEvent(
                        this.getTaggingData(
                          `message box error ${error?.failedService}`,
                          BILLING_SERVICES_ERROR
                        )
                      );
                    }
                  }
                })
              );
          })
        )
        .subscribe()
    );
  }

  private setPageTitle() {
    const userType = this.isResUser ? 'RES' : 'SE';
    this.mainTitle = this.translationService.getTranslatedValueFor(
      `bills.billsScreen.titles.${userType}`
    );
  }

  private updateSelectedAccountWhenThereIsParameter() {
    if (
      this.billingAccountNumberUrlParam &&
      !this.hasAccountSelectionFromUrlParamBeenDispatched
    ) {
      this.hasAccountSelectionFromUrlParamBeenDispatched = true;
      this.store.dispatch(
        setSelectedBillingAccount({
          billingAccountNumber: this.billingAccountNumberUrlParam,
        })
      );
    }
  }

  private dispatchPaymentStatusAlert(status: string) {
    if (paymentService.isPaymentSuccessful(status)) {
      this.messageBoxType = MessageBoxTypesEnum.Success;
      this.messageBoxTitleKey = 'bills.toast.payment.successTitle';
      this.messageBoxContentKey = 'bills.toast.payment.success';
      this.analytics.reportEvent(
        this.getTaggingData('info message: successful payment')
      );
    } else {
      this.messageBoxType = MessageBoxTypesEnum.Error;
      this.messageBoxTitleKey = 'bills.toast.payment.failureTitle';
      this.messageBoxContentKey = 'bills.toast.payment.failure';
      this.analytics.reportEvent(
        this.getTaggingData(
          'message box error',
          'the payment has not been successful'
        )
      );
    }
  }

  private getTaggingData(name: string, errorMessage?: string) {
    return {
      event: 'popup-impression',
      popup: {
        zone: 'mybill.overview',
        name,
      },
      ...(errorMessage && {
        error: {
          type: 'front-end',
          message: errorMessage,
        },
      }),
    };
  }
}
